import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { RETURN_SORT_KEYS, ReturnStatus, type DisputeOutcome, type ReturnDetail, type ReturnRequest, type ReturnSortKey } from "@/api";
import { useReceiveReturn, useRefundReturn, useResolveDispute, useResolveReturn, useReturnDetail, useReturnPage, useReturnsSummary } from "@/api/hooks/use-returns";
import { PaymentRefundRow } from "@/components/payment-refund-panel";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store/auth";
import { Input } from "@osmix/ui/input";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { FilterChips, type FilterChip } from "@/components/filter-chips";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { serverDataTable, useUrlTable } from "@/lib/use-url-table";
import { orderNo } from "@/lib/short-numbers";
import { formatDateTime } from "@/lib/utils";

// Требование задачи: суммы в «руб.», не «₽». Общий formatPrice печатает знак
// рубля, поэтому у возвратов свой формат — тысячи с неразрывным пробелом.
const formatRub = (n: number): string => `${new Intl.NumberFormat("ru-RU").format(Math.round(n))} руб.`;
const STATUS_VARIANT: Record<ReturnStatus, "default" | "secondary" | "destructive" | "outline"> = {
  requested: "secondary",
  approved: "default",
  received: "default",
  refunded: "outline",
  rejected: "destructive"
};
const CHIP_STATUSES = ReturnStatus.options;
const isOpenDispute = (r: ReturnRequest) => r.kind === "dispute" || r.dispute?.status === "open";
export function ReturnsPage() {
  const {
    t
  } = useTranslation();
  // Статус, «открытые споры», поиск, сортировка и страница — на сервере,
  // срез — в адресной строке.
  const url = useUrlTable<ReturnSortKey>({
    filters: ["status", "search", "dispute"],
    defaultSort: {
      key: "date",
      dir: "desc"
    },
    defaultPageSize: 25,
    sortKeys: RETURN_SORT_KEYS
  });
  const status = (ReturnStatus.options as string[]).includes(url.filters.status ?? "") ? url.filters.status as ReturnStatus : "";
  const disputesOnly = url.filters.dispute === "open";
  const {
    data,
    error: loadError
  } = useReturnPage({
    status: status || undefined,
    search: url.filters.search || undefined,
    openDisputes: disputesOnly || undefined,
    sort: url.sort.key,
    dir: url.sort.dir,
    page: url.page,
    pageSize: url.pageSize
  });
  const summary = useReturnsSummary().data;
  const error = loadError ? loadError.message : null;
  const rows = data?.items ?? [];
  const table = serverDataTable(url, rows, data?.total ?? 0);

  // `?return=<id>` — ссылка из карточки заказа. Заявка может быть не на
  // текущей странице, поэтому карточка держит id, а данные берёт со страницы
  // или из полной карточки с сервера.
  const [params, setParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(params.get("return"));
  const linkedId = params.get("return");
  useEffect(() => {
    if (linkedId) setSelectedId(linkedId);
  }, [linkedId]);
  const closeDetail = () => {
    setSelectedId(null);
    if (params.get("return")) {
      setParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete("return");
        return next;
      }, {
        replace: true
      });
    }
  };

  // Отражаем в диалоге актуальную заявку после мутации: страница
  // перечитывается, и открытая карточка должна показывать новую стадию.
  const linkedDetail = useReturnDetail(selectedId && !rows.some(r => r.id === selectedId) ? selectedId : null);
  const selectedLive: ReturnRequest | null = selectedId ? rows.find(r_0 => r_0.id === selectedId) ?? linkedDetail.data ?? null : null;

  // Цифры на чипах и на кнопке споров — из серверной сводки по всем
  // заявкам, не по странице.
  const counts = new Map((summary?.statuses ?? []).map(s => [s.status, s.count]));
  const chips: FilterChip<ReturnStatus>[] = CHIP_STATUSES.map(value => ({
    value,
    label: t(`returns.status.${value}`),
    count: counts.get(value) ?? 0,
    tone: value === "requested" ? "warning" : "default"
  }));
  const [searchInput, setSearchInput] = useState(url.filters.search ?? "");
  const urlSearch = url.filters.search ?? "";
  useEffect(() => setSearchInput(urlSearch), [urlSearch]);
  const setFilterRef = useRef(url.setFilter);
  setFilterRef.current = url.setFilter;
  useEffect(() => {
    if (searchInput.trim() === urlSearch) return;
    const id = setTimeout(() => setFilterRef.current("search", searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput, urlSearch]);
  if (error) return <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
				{error}
			</div>;
  if (data === undefined) return <p className="text-muted-foreground">{t("returns.loading")}</p>;
  return <div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h1 className="text-2xl font-bold">{t("nav.returns")}</h1>
				{summary && summary.overdue > 0 && <Badge variant="destructive">
						{t("returns.overdueCount", {
          count: summary.overdue
        })}
					</Badge>}
			</div>

			<FilterChips value={status} chips={chips} onChange={next_0 => url.setFilter("status", next_0)} allLabel={t("returns.filter.all")} allCount={summary?.total ?? 0} />

			<div className="flex flex-wrap items-center gap-2">
				<Input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder={t("returns.searchPlaceholder")} aria-label={t("returns.searchPlaceholder")} className="h-9 w-full sm:max-w-sm" />
				<Button size="sm" variant={disputesOnly ? "default" : "outline"} aria-pressed={disputesOnly} data-testid="returns-filter-disputes" onClick={() => url.setFilter("dispute", disputesOnly ? "" : "open")}>
					{t("returns.filter.disputes", {
          count: summary?.openDisputes ?? 0
        })}
				</Button>
			</div>

			{table.total === 0 && !url.hasFilters ? <p className="text-muted-foreground">{t("returns.empty")}</p> : table.total === 0 ? <p className="text-muted-foreground">{t("returns.filterEmpty")}</p> : <div className="flex flex-col gap-4">
					<Table>
						<TableHeader>
							<TableRow>
								<SortableHead label={t("returns.col.order")} sortKey="order" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("returns.col.date")} sortKey="date" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("returns.col.shop")} sortKey="shop" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("returns.col.buyer")} sortKey="buyer" sort={table.sort} onToggle={table.toggleSort} />
								<TableHead>{t("returns.col.kind")}</TableHead>
								<SortableHead label={t("returns.col.total")} sortKey="total" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" />
								<SortableHead label={t("returns.col.due")} sortKey="due" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("returns.col.status")} sortKey="status" sort={table.sort} onToggle={table.toggleSort} />
							</TableRow>
						</TableHeader>
						<TableBody>
							{table.pageRows.map(r_1 => <TableRow key={r_1.id} className="cursor-pointer" onClick={() => setSelectedId(r_1.id)}>
									<TableCell className="whitespace-nowrap font-medium tabular-nums" title={r_1.orderId}>
										{orderNo(r_1.orderId, r_1.orderNumber)}
									</TableCell>
									<TableCell className="text-muted-foreground">{formatDateTime(r_1.createdAt)}</TableCell>
									<TableCell>{r_1.shopName ?? t("returns.multiShop")}</TableCell>
									<TableCell>
										<p className="font-medium">{r_1.buyerName ?? "—"}</p>
										{r_1.buyerNumber ? <p className="text-xs text-muted-foreground tabular-nums">
												{t("returns.buyerNumber", {
                  number: r_1.buyerNumber
                })}
											</p> : null}
									</TableCell>
									<TableCell>
										<Badge variant={isOpenDispute(r_1) ? "destructive" : "secondary"}>
											{isOpenDispute(r_1) ? t("returns.kind.dispute") : t(`returns.kind.${r_1.kind}`)}
										</Badge>
									</TableCell>
									<TableCell className="text-right font-medium">{formatRub(r_1.refundTotal)}</TableCell>
									<TableCell className="whitespace-nowrap text-sm">
										<DueCell request={r_1} />
									</TableCell>
									<TableCell>
										<Badge variant={STATUS_VARIANT[r_1.status]}>{t(`returns.status.${r_1.status}`)}</Badge>
									</TableCell>
								</TableRow>)}
						</TableBody>
					</Table>
					<DataTablePagination table={table} />
				</div>}

			<ReturnDetailDialog request={selectedLive} onClose={closeDetail} />
		</div>;
}

// Срок рассмотрения (fd-order-20): 4 рабочих дня. Показываем «просрочено» у
// нерассмотренных, иначе — сколько дней осталось; у закрытых заявок только дату.
function DueCell(t0) {
  const $ = _c(15);
  const {
    request
  } = t0;
  const {
    t
  } = useTranslation();
  if (!request.dueAt) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="text-muted-foreground">—</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (request.overdue) {
    let t1;
    if ($[1] !== t) {
      t1 = t("returns.overdue");
      $[1] = t;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== t1) {
      t2 = <Badge variant="destructive">{t1}</Badge>;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (request.status === "requested") {
    const t1 = request.daysLeft <= 1 ? "font-medium text-amber-600 dark:text-amber-500" : "text-muted-foreground";
    const t2 = Math.max(request.daysLeft, 0);
    let t3;
    if ($[5] !== t || $[6] !== t2) {
      t3 = t("returns.daysLeft", {
        count: t2
      });
      $[5] = t;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t1 || $[9] !== t3) {
      t4 = <span className={t1}>{t3}</span>;
      $[8] = t1;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t1;
  if ($[11] !== request.dueAt) {
    t1 = formatDateTime(request.dueAt);
    $[11] = request.dueAt;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== t1) {
    t2 = <span className="text-muted-foreground">{t1}</span>;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
function ReturnDetailDialog(t0) {
  const $ = _c(250);
  const {
    request,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const user = useAuth(_temp);
  let t1;
  if ($[0] !== user) {
    t1 = hasAdminPermission(user, "action.orders.manage");
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canManage = t1;
  const resolve = useResolveReturn();
  const receive = useReceiveReturn();
  const refund = useRefundReturn();
  const resolveDispute = useResolveDispute();
  const detail = useReturnDetail(request?.id ?? null);
  const full = detail.data;
  const [mode, setMode] = useState(null);
  const [justified, setJustified] = useState(false);
  const [note, setNote] = useState("");
  const [outcome, setOutcome] = useState(null);
  const [disputeNote, setDisputeNote] = useState("");
  const [confirmRefund, setConfirmRefund] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setMode(null);
      setJustified(false);
      setNote("");
      setOutcome(null);
      setDisputeNote("");
      setConfirmRefund(false);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const reset = t2;
  let t3;
  if ($[3] !== onClose) {
    t3 = () => {
      reset();
      onClose();
    };
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const close = t3;
  if (!request) {
    return null;
  }
  const busy = resolve.isPending || receive.isPending || refund.isPending || resolveDispute.isPending;
  const actionError = resolve.error || receive.error || refund.error || resolveDispute.error;
  const dispute = full?.dispute ?? request.dispute;
  let t4;
  if ($[5] !== detail) {
    t4 = () => detail.refetch();
    $[5] = detail;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const refetchDetail = t4;
  let t5;
  if ($[7] !== justified || $[8] !== mode || $[9] !== note || $[10] !== refetchDetail || $[11] !== request.id || $[12] !== resolve) {
    t5 = () => {
      resolve.mutate({
        id: request.id,
        input: mode === "approve" ? {
          approve: true,
          justified,
          note: note || undefined
        } : {
          approve: false,
          note: note || undefined
        }
      }, {
        onSuccess: () => {
          reset();
          refetchDetail();
        }
      });
    };
    $[7] = justified;
    $[8] = mode;
    $[9] = note;
    $[10] = refetchDetail;
    $[11] = request.id;
    $[12] = resolve;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const submitResolve = t5;
  let t6;
  if ($[14] !== full?.attachments) {
    t6 = full?.attachments.filter(_temp2) ?? [];
    $[14] = full?.attachments;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const buyerPhotos = t6;
  let t7;
  if ($[16] !== full?.attachments) {
    t7 = full?.attachments.filter(_temp3) ?? [];
    $[16] = full?.attachments;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const sellerPhotos = t7;
  let t8;
  if ($[18] !== close) {
    t8 = open => !open && close();
    $[18] = close;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = `/orders?order=${request.orderId}`;
  const t10 = request.orderId;
  let t11;
  if ($[20] !== request.orderId || $[21] !== request.orderNumber || $[22] !== t) {
    t11 = t("returns.orderTitle", {
      number: orderNo(request.orderId, request.orderNumber)
    });
    $[20] = request.orderId;
    $[21] = request.orderNumber;
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== request.orderId || $[25] !== t11 || $[26] !== t9) {
    t12 = <Link to={t9} className="text-sm text-primary tabular-nums hover:underline" title={t10}>{t11}</Link>;
    $[24] = request.orderId;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = STATUS_VARIANT[request.status];
  const t14 = `returns.status.${request.status}`;
  let t15;
  if ($[28] !== t || $[29] !== t14) {
    t15 = t(t14);
    $[28] = t;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t13 || $[32] !== t15) {
    t16 = <Badge variant={t13}>{t15}</Badge>;
    $[31] = t13;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== dispute || $[35] !== request.kind || $[36] !== t) {
    t17 = dispute ? <Badge variant={dispute.status === "open" ? "destructive" : "outline"}>{dispute.status === "open" ? t("returns.dispute.open") : t("returns.dispute.closed")}</Badge> : <Badge variant="secondary">{t(`returns.kind.${request.kind}`)}</Badge>;
    $[34] = dispute;
    $[35] = request.kind;
    $[36] = t;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t12 || $[39] !== t16 || $[40] !== t17) {
    t18 = <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2 pr-6">{t12}{t16}{t17}</DialogTitle></DialogHeader>;
    $[38] = t12;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t("returns.detail.shop");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== request.shopName || $[45] !== t) {
    t20 = request.shopName ?? t("returns.multiShop");
    $[44] = request.shopName;
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t20) {
    t21 = <p>{t20}</p>;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t19 || $[50] !== t21) {
    t22 = <Field label={t19}>{t21}</Field>;
    $[49] = t19;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t) {
    t23 = t("returns.detail.buyer");
    $[52] = t;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== request.buyerName || $[55] !== request.buyerUserId) {
    t24 = request.buyerUserId ? <Link to={`/users/${request.buyerUserId}`} className="text-primary hover:underline">{request.buyerName ?? request.buyerUserId}</Link> : <p>{request.buyerName ?? "\u2014"}</p>;
    $[54] = request.buyerName;
    $[55] = request.buyerUserId;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== request.buyerNumber || $[58] !== t) {
    t25 = request.buyerNumber ? <p className="text-xs text-muted-foreground tabular-nums">{t("returns.buyerNumber", {
        number: request.buyerNumber
      })}</p> : null;
    $[57] = request.buyerNumber;
    $[58] = t;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t23 || $[61] !== t24 || $[62] !== t25) {
    t26 = <Field label={t23}>{t24}{t25}</Field>;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t) {
    t27 = t("returns.detail.created");
    $[64] = t;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== request.createdAt) {
    t28 = formatDateTime(request.createdAt);
    $[66] = request.createdAt;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t28) {
    t29 = <p>{t28}</p>;
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== t27 || $[71] !== t29) {
    t30 = <Field label={t27}>{t29}</Field>;
    $[70] = t27;
    $[71] = t29;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] !== t) {
    t31 = t("returns.detail.due");
    $[73] = t;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== request) {
    t32 = <DueCell request={request} />;
    $[75] = request;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== t31 || $[78] !== t32) {
    t33 = <Field label={t31}>{t32}</Field>;
    $[77] = t31;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== t22 || $[81] !== t26 || $[82] !== t30 || $[83] !== t33) {
    t34 = <div className="grid gap-4 sm:grid-cols-2">{t22}{t26}{t30}{t33}</div>;
    $[80] = t22;
    $[81] = t26;
    $[82] = t30;
    $[83] = t33;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== t) {
    t35 = t("returns.detail.reason");
    $[85] = t;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== request.reasonCode || $[88] !== t) {
    t36 = request.reasonCode && <p className="font-medium" data-testid="return-reason-code">{t(`returns.reasonCode.${request.reasonCode}`, {
        defaultValue: request.reasonCode
      })}</p>;
    $[87] = request.reasonCode;
    $[88] = t;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] !== request.reason) {
    t37 = <p className="whitespace-pre-wrap">{request.reason}</p>;
    $[90] = request.reason;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] !== t35 || $[93] !== t36 || $[94] !== t37) {
    t38 = <Field label={t35}>{t36}{t37}</Field>;
    $[92] = t35;
    $[93] = t36;
    $[94] = t37;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] !== full || $[97] !== t) {
    t39 = full && full.lines.length > 0 && <Field label={t("returns.detail.lines")}><div className="flex flex-col gap-2" data-testid="return-lines">{full.lines.map(line => <div key={line.id} className="flex items-start gap-3 rounded-md border p-2"><div className="size-12 shrink-0 overflow-hidden rounded bg-muted">{line.photo && <img src={line.photo} alt="" className="size-full object-cover" />}</div><div className="min-w-0 flex-1"><Link to={`/products/${line.productId}`} className="block truncate hover:underline">{line.name}</Link><p className="text-xs text-muted-foreground">{line.quantity} × {formatRub(line.unitPrice)}{line.variant ? ` · ${line.variant}` : ""}{" \xB7 "}{t(`returns.reasonCode.${line.reasonCode}`, {
                defaultValue: line.reasonCode
              })}</p>{line.comment && <p className="text-xs italic text-muted-foreground">«{line.comment}»</p>}{line.cancelledAt && <p className="text-xs text-muted-foreground">{t("returns.detail.lineCancelled")}</p>}</div></div>)}</div></Field>;
    $[96] = full;
    $[97] = t;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  let t40;
  if ($[99] !== buyerPhotos || $[100] !== sellerPhotos || $[101] !== t) {
    t40 = (buyerPhotos.length > 0 || sellerPhotos.length > 0) && <div className="grid gap-4 sm:grid-cols-2"><Photos label={t("returns.detail.buyerPhotos")} items={buyerPhotos} /><Photos label={t("returns.detail.sellerPhotos")} items={sellerPhotos} /></div>;
    $[99] = buyerPhotos;
    $[100] = sellerPhotos;
    $[101] = t;
    $[102] = t40;
  } else {
    t40 = $[102];
  }
  let t41;
  if ($[103] !== busy || $[104] !== canManage || $[105] !== dispute || $[106] !== disputeNote || $[107] !== outcome || $[108] !== refetchDetail || $[109] !== request.id || $[110] !== resolveDispute || $[111] !== t) {
    t41 = dispute && <div className="flex flex-col gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3" data-testid="return-dispute"><p className="text-xs font-medium uppercase text-muted-foreground">{t("returns.dispute.title")}</p><p><span className="font-medium">{t("returns.dispute.buyer")}: </span><span className="whitespace-pre-wrap">{dispute.buyerComment}</span></p><p><span className="font-medium">{t("returns.dispute.seller")}: </span>{dispute.sellerText ? <span className="whitespace-pre-wrap">{dispute.sellerText}</span> : <span className="text-muted-foreground">{t("returns.dispute.noSellerAnswer")}</span>}</p>{dispute.outcome && <p className="font-medium">{t(`returns.dispute.outcome.${dispute.outcome}`)}</p>}{dispute.status === "open" && canManage && <div className="flex flex-col gap-2 border-t pt-2"><div className="flex flex-wrap gap-2">{(["buyer", "seller", "partial"] as const).map(value => <Button key={value} size="sm" variant={outcome === value ? "default" : "outline"} onClick={() => setOutcome(value)} disabled={busy}>{t(`returns.dispute.choose.${value}`)}</Button>)}</div>{outcome && <><textarea className="min-h-16 rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50" value={disputeNote} onChange={e => setDisputeNote(e.target.value)} placeholder={t("returns.dispute.notePlaceholder")} /><p className="text-xs text-muted-foreground">{t(`returns.dispute.hint.${outcome}`)}</p><div className="flex flex-wrap gap-2"><Button size="sm" disabled={busy || !disputeNote.trim()} onClick={() => resolveDispute.mutate({
              id: request.id,
              outcome,
              note: disputeNote.trim()
            }, {
              onSuccess: () => {
                reset();
                refetchDetail();
              }
            })}>{t("returns.dispute.submit")}</Button><Button size="sm" variant="ghost" onClick={() => setOutcome(null)} disabled={busy}>{t("confirm.cancel")}</Button></div></>}</div>}</div>;
    $[103] = busy;
    $[104] = canManage;
    $[105] = dispute;
    $[106] = disputeNote;
    $[107] = outcome;
    $[108] = refetchDetail;
    $[109] = request.id;
    $[110] = resolveDispute;
    $[111] = t;
    $[112] = t41;
  } else {
    t41 = $[112];
  }
  let t42;
  if ($[113] !== full || $[114] !== request.status || $[115] !== t) {
    t42 = request.status === "requested" && typeof full?.requestedRefund === "number" && <Row label={t("returns.detail.requested")} value={formatRub(full.requestedRefund)} />;
    $[113] = full;
    $[114] = request.status;
    $[115] = t;
    $[116] = t42;
  } else {
    t42 = $[116];
  }
  let t43;
  if ($[117] !== t) {
    t43 = t("returns.detail.refundGoods");
    $[117] = t;
    $[118] = t43;
  } else {
    t43 = $[118];
  }
  let t44;
  if ($[119] !== request.refundGoods) {
    t44 = formatRub(request.refundGoods);
    $[119] = request.refundGoods;
    $[120] = t44;
  } else {
    t44 = $[120];
  }
  let t45;
  if ($[121] !== t43 || $[122] !== t44) {
    t45 = <Row label={t43} value={t44} />;
    $[121] = t43;
    $[122] = t44;
    $[123] = t45;
  } else {
    t45 = $[123];
  }
  let t46;
  if ($[124] !== t) {
    t46 = t("returns.detail.refundDelivery");
    $[124] = t;
    $[125] = t46;
  } else {
    t46 = $[125];
  }
  let t47;
  if ($[126] !== request.refundDelivery) {
    t47 = formatRub(request.refundDelivery);
    $[126] = request.refundDelivery;
    $[127] = t47;
  } else {
    t47 = $[127];
  }
  let t48;
  if ($[128] !== t46 || $[129] !== t47) {
    t48 = <Row label={t46} value={t47} />;
    $[128] = t46;
    $[129] = t47;
    $[130] = t48;
  } else {
    t48 = $[130];
  }
  let t49;
  if ($[131] !== t) {
    t49 = t("returns.detail.refundTotal");
    $[131] = t;
    $[132] = t49;
  } else {
    t49 = $[132];
  }
  let t50;
  if ($[133] !== t49) {
    t50 = <span>{t49}</span>;
    $[133] = t49;
    $[134] = t50;
  } else {
    t50 = $[134];
  }
  let t51;
  if ($[135] !== request.refundTotal) {
    t51 = formatRub(request.refundTotal);
    $[135] = request.refundTotal;
    $[136] = t51;
  } else {
    t51 = $[136];
  }
  let t52;
  if ($[137] !== t51) {
    t52 = <span>{t51}</span>;
    $[137] = t51;
    $[138] = t52;
  } else {
    t52 = $[138];
  }
  let t53;
  if ($[139] !== t50 || $[140] !== t52) {
    t53 = <div className="flex justify-between border-t pt-1 text-base font-bold">{t50}{t52}</div>;
    $[139] = t50;
    $[140] = t52;
    $[141] = t53;
  } else {
    t53 = $[141];
  }
  let t54;
  if ($[142] !== request.justified || $[143] !== t) {
    t54 = typeof request.justified === "boolean" && <p className="pt-1 text-xs text-muted-foreground">{request.justified ? t("returns.detail.justifiedYes") : t("returns.detail.justifiedNo")}</p>;
    $[142] = request.justified;
    $[143] = t;
    $[144] = t54;
  } else {
    t54 = $[144];
  }
  let t55;
  if ($[145] !== t42 || $[146] !== t45 || $[147] !== t48 || $[148] !== t53 || $[149] !== t54) {
    t55 = <div className="flex flex-col gap-1 rounded-md border p-3">{t42}{t45}{t48}{t53}{t54}</div>;
    $[145] = t42;
    $[146] = t45;
    $[147] = t48;
    $[148] = t53;
    $[149] = t54;
    $[150] = t55;
  } else {
    t55 = $[150];
  }
  let t56;
  if ($[151] !== request.paymentRefund || $[152] !== request.providerRefund?.api || $[153] !== request.refundProvider || $[154] !== request.status || $[155] !== t) {
    t56 = request.providerRefund?.api && !request.paymentRefund && !request.refundProvider && request.status !== "rejected" && request.status !== "refunded" && <p className="rounded-md border p-2 text-xs text-muted-foreground" data-testid="robokassa-api-hint">{t("paymentRefunds.apiHint")}</p>;
    $[151] = request.paymentRefund;
    $[152] = request.providerRefund?.api;
    $[153] = request.refundProvider;
    $[154] = request.status;
    $[155] = t;
    $[156] = t56;
  } else {
    t56 = $[156];
  }
  let t57;
  if ($[157] !== canManage || $[158] !== request.paymentRefund) {
    t57 = request.paymentRefund && <PaymentRefundRow refund={request.paymentRefund} canManage={canManage} />;
    $[157] = canManage;
    $[158] = request.paymentRefund;
    $[159] = t57;
  } else {
    t57 = $[159];
  }
  let t58;
  if ($[160] !== request.orderId || $[161] !== request.orderNumber || $[162] !== request.paymentRefund?.status || $[163] !== request.providerRefund || $[164] !== request.refundProvider || $[165] !== request.status || $[166] !== t) {
    t58 = request.providerRefund && !request.refundProvider && request.status !== "rejected" && (!request.providerRefund.api || request.paymentRefund?.status === "failed" || request.paymentRefund?.status === "uncertain") && <p className="rounded-md border border-amber-300/60 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200" data-testid="robokassa-refund-hint">{t(request.providerRefund.invoiceId != null ? "returns.detail.robokassaRefund" : "returns.detail.robokassaRefundNoInvoice", {
        order: request.providerRefund.orderNumber ?? request.orderNumber ?? request.orderId,
        invoice: request.providerRefund.invoiceId,
        amount: formatRub(request.providerRefund.amount)
      })}</p>;
    $[160] = request.orderId;
    $[161] = request.orderNumber;
    $[162] = request.paymentRefund?.status;
    $[163] = request.providerRefund;
    $[164] = request.refundProvider;
    $[165] = request.status;
    $[166] = t;
    $[167] = t58;
  } else {
    t58 = $[167];
  }
  let t59;
  if ($[168] !== request.goodsReceivedAt || $[169] !== request.refundProvider || $[170] !== request.refundTransactionId || $[171] !== request.refundedAt || $[172] !== request.resolutionNote || $[173] !== request.resolvedAt || $[174] !== request.resolvedByName || $[175] !== t) {
    t59 = (request.resolvedByName || request.resolutionNote || request.goodsReceivedAt || request.refundedAt) && <div className="flex flex-col gap-1 text-xs text-muted-foreground">{request.resolvedByName && request.resolvedAt && <p>{t("returns.detail.resolvedBy", {
          name: request.resolvedByName,
          at: formatDateTime(request.resolvedAt)
        })}</p>}{request.resolutionNote && <p className="italic">«{request.resolutionNote}»</p>}{request.goodsReceivedAt && <p>{t("returns.detail.goodsReceivedAt", {
          at: formatDateTime(request.goodsReceivedAt)
        })}</p>}{request.refundedAt && <p>{t("returns.detail.refundedAt", {
          at: formatDateTime(request.refundedAt)
        })}</p>}{request.refundedAt && <p>{request.refundProvider ? t("returns.detail.refundViaPsp", {
          tx: request.refundTransactionId ?? "\u2014"
        }) : t("returns.detail.refundManual")}</p>}</div>;
    $[168] = request.goodsReceivedAt;
    $[169] = request.refundProvider;
    $[170] = request.refundTransactionId;
    $[171] = request.refundedAt;
    $[172] = request.resolutionNote;
    $[173] = request.resolvedAt;
    $[174] = request.resolvedByName;
    $[175] = t;
    $[176] = t59;
  } else {
    t59 = $[176];
  }
  let t60;
  if ($[177] !== actionError) {
    t60 = actionError && <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">{actionError.message}</p>;
    $[177] = actionError;
    $[178] = t60;
  } else {
    t60 = $[178];
  }
  let t61;
  if ($[179] !== canManage || $[180] !== request.status || $[181] !== t) {
    t61 = !canManage && request.status !== "rejected" && request.status !== "refunded" && <p className="text-xs text-muted-foreground">{t("returns.readOnly")}</p>;
    $[179] = canManage;
    $[180] = request.status;
    $[181] = t;
    $[182] = t61;
  } else {
    t61 = $[182];
  }
  let t62;
  if ($[183] !== busy || $[184] !== canManage || $[185] !== mode || $[186] !== request.status || $[187] !== t) {
    t62 = canManage && request.status === "requested" && mode === null && <div className="flex flex-wrap gap-2"><Button onClick={() => setMode("approve")} disabled={busy}>{t("returns.action.approve")}</Button><Button variant="destructive" onClick={() => setMode("reject")} disabled={busy}>{t("returns.action.reject")}</Button></div>;
    $[183] = busy;
    $[184] = canManage;
    $[185] = mode;
    $[186] = request.status;
    $[187] = t;
    $[188] = t62;
  } else {
    t62 = $[188];
  }
  let t63;
  if ($[189] !== busy || $[190] !== canManage || $[191] !== justified || $[192] !== mode || $[193] !== note || $[194] !== request.status || $[195] !== submitResolve || $[196] !== t) {
    t63 = canManage && request.status === "requested" && mode !== null && <div className="flex flex-col gap-3 rounded-md border p-3">{mode === "approve" && <label className="flex items-start gap-2"><input type="checkbox" className="mt-0.5 size-4" checked={justified} onChange={e_0 => setJustified(e_0.target.checked)} /><span><span className="font-medium">{t("returns.action.justified")}</span><span className="block text-xs text-muted-foreground">{t("returns.action.justifiedHint")}</span></span></label>}<label className="flex flex-col gap-1"><span className="text-xs font-medium text-muted-foreground">{t("returns.action.note")}</span><textarea className="min-h-16 rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50" value={note} onChange={e_1 => setNote(e_1.target.value)} placeholder={t("returns.action.notePlaceholder")} /></label><div className="flex flex-wrap gap-2"><Button variant={mode === "reject" ? "destructive" : "default"} onClick={submitResolve} disabled={busy}>{mode === "approve" ? t("returns.action.confirmApprove") : t("returns.action.confirmReject")}</Button><Button variant="ghost" onClick={reset} disabled={busy}>{t("confirm.cancel")}</Button></div></div>;
    $[189] = busy;
    $[190] = canManage;
    $[191] = justified;
    $[192] = mode;
    $[193] = note;
    $[194] = request.status;
    $[195] = submitResolve;
    $[196] = t;
    $[197] = t63;
  } else {
    t63 = $[197];
  }
  let t64;
  if ($[198] !== busy || $[199] !== canManage || $[200] !== receive || $[201] !== request.id || $[202] !== request.status || $[203] !== t) {
    t64 = canManage && request.status === "approved" && <div className="flex flex-wrap gap-2"><Button onClick={() => receive.mutate(request.id)} disabled={busy}>{t("returns.action.received")}</Button></div>;
    $[198] = busy;
    $[199] = canManage;
    $[200] = receive;
    $[201] = request.id;
    $[202] = request.status;
    $[203] = t;
    $[204] = t64;
  } else {
    t64 = $[204];
  }
  let t65;
  if ($[205] !== busy || $[206] !== canManage || $[207] !== request.paymentRefund || $[208] !== request.status || $[209] !== t) {
    t65 = canManage && request.status === "received" && !request.paymentRefund && <div className="flex flex-wrap gap-2"><Button onClick={() => setConfirmRefund(true)} disabled={busy}>{t("returns.action.refunded")}</Button></div>;
    $[205] = busy;
    $[206] = canManage;
    $[207] = request.paymentRefund;
    $[208] = request.status;
    $[209] = t;
    $[210] = t65;
  } else {
    t65 = $[210];
  }
  let t66;
  if ($[211] !== t34 || $[212] !== t38 || $[213] !== t39 || $[214] !== t40 || $[215] !== t41 || $[216] !== t55 || $[217] !== t56 || $[218] !== t57 || $[219] !== t58 || $[220] !== t59 || $[221] !== t60 || $[222] !== t61 || $[223] !== t62 || $[224] !== t63 || $[225] !== t64 || $[226] !== t65) {
    t66 = <div className="flex min-w-0 flex-col gap-5 text-sm">{t34}{t38}{t39}{t40}{t41}{t55}{t56}{t57}{t58}{t59}{t60}{t61}{t62}{t63}{t64}{t65}</div>;
    $[211] = t34;
    $[212] = t38;
    $[213] = t39;
    $[214] = t40;
    $[215] = t41;
    $[216] = t55;
    $[217] = t56;
    $[218] = t57;
    $[219] = t58;
    $[220] = t59;
    $[221] = t60;
    $[222] = t61;
    $[223] = t62;
    $[224] = t63;
    $[225] = t64;
    $[226] = t65;
    $[227] = t66;
  } else {
    t66 = $[227];
  }
  let t67;
  if ($[228] !== request.refundTotal || $[229] !== t) {
    t67 = t("returns.action.refundTitle", {
      amount: formatRub(request.refundTotal)
    });
    $[228] = request.refundTotal;
    $[229] = t;
    $[230] = t67;
  } else {
    t67 = $[230];
  }
  let t68;
  if ($[231] !== t) {
    t68 = t("returns.action.refundDescription");
    $[231] = t;
    $[232] = t68;
  } else {
    t68 = $[232];
  }
  let t69;
  if ($[233] !== refund || $[234] !== request.id) {
    t69 = () => refund.mutate(request.id, {
      onSuccess: () => setConfirmRefund(false),
      onError: () => setConfirmRefund(false)
    });
    $[233] = refund;
    $[234] = request.id;
    $[235] = t69;
  } else {
    t69 = $[235];
  }
  let t70;
  if ($[236] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = () => setConfirmRefund(false);
    $[236] = t70;
  } else {
    t70 = $[236];
  }
  let t71;
  if ($[237] !== confirmRefund || $[238] !== refund.isPending || $[239] !== t67 || $[240] !== t68 || $[241] !== t69) {
    t71 = <ConfirmDialog open={confirmRefund} title={t67} description={t68} busy={refund.isPending} onConfirm={t69} onClose={t70} />;
    $[237] = confirmRefund;
    $[238] = refund.isPending;
    $[239] = t67;
    $[240] = t68;
    $[241] = t69;
    $[242] = t71;
  } else {
    t71 = $[242];
  }
  let t72;
  if ($[243] !== t18 || $[244] !== t66 || $[245] !== t71) {
    t72 = <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">{t18}{t66}{t71}</DialogContent>;
    $[243] = t18;
    $[244] = t66;
    $[245] = t71;
    $[246] = t72;
  } else {
    t72 = $[246];
  }
  let t73;
  if ($[247] !== t72 || $[248] !== t8) {
    t73 = <Dialog open={true} onOpenChange={t8}>{t72}</Dialog>;
    $[247] = t72;
    $[248] = t8;
    $[249] = t73;
  } else {
    t73 = $[249];
  }
  return t73;
}
function _temp3(a_0) {
  return a_0.author === "seller";
}
function _temp2(a) {
  return a.author === "buyer";
}
function _temp(state) {
  return state.user;
}
function Photos(t0) {
  const $ = _c(6);
  const {
    label,
    items
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== items || $[1] !== t) {
    t1 = items.length === 0 ? <p className="text-xs text-muted-foreground">{t("returns.detail.noPhotos")}</p> : <div className="flex flex-wrap gap-2">{items.map(a => <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="block size-16 overflow-hidden rounded border bg-muted">{a.kind === "photo" ? <img src={a.thumbnailUrl || a.url} alt="" className="size-full object-cover" /> : <span className="flex size-full items-center justify-center text-xs">{t("returns.detail.video")}</span>}</a>)}</div>;
    $[0] = items;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== label || $[4] !== t1) {
    t2 = <Field label={label}>{t1}</Field>;
    $[3] = label;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function Field(t0) {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div>{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function Row(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span>{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-foreground">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex justify-between text-muted-foreground">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}